import { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Button, IconButton, TextField, Checkbox, FormControlLabel, Chip,
  CircularProgress, Tooltip, Paper, Divider, MenuItem, Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  UploadFile as UploadIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import api from '../../config/api';
import useStore from '../../config/store';
import useWhatsappChannel from '../../hooks/useWhatsappChannel';

/**
 * Campañas push (F5, Fase A): carga del Excel de contactos con preview y
 * confirmación. El ENVÍO se habilita en Fase B (plantillas de Meta + pago) —
 * aquí las bases quedan cargadas, normalizadas y deduplicadas, en estado
 * "pendiente de envío".
 * nombre / origen / opt-in son OPCIONALES; cada campo explica para qué sirve.
 */
const statusChip = {
  draft: { color: '#92400E', bg: '#FEF3C7' },
  ready: { color: '#4C1D95', bg: '#EDE9FE' },
  sending: { color: '#1D4ED8', bg: '#DBEAFE' },
  paused_tier: { color: '#92400E', bg: '#FEF3C7' },
  completed: { color: '#16A34A', bg: '#DCFCE7' },
};

// Colores del estado por contacto (detalle de envíos)
const contactStatusChip = {
  pending: { color: '#374151', bg: '#F3F4F6' },
  sent: { color: '#1D4ED8', bg: '#DBEAFE' },
  delivered: { color: '#4C1D95', bg: '#EDE9FE' },
  read: { color: '#0F766E', bg: '#CCFBF1' },
  replied: { color: '#16A34A', bg: '#DCFCE7' },
  declined: { color: '#92400E', bg: '#FEF3C7' },
  failed: { color: '#991B1B', bg: '#FEE2E2' },
  skipped_optout: { color: '#92400E', bg: '#FEF3C7' },
  skipped_veto: { color: '#92400E', bg: '#FEF3C7' },
  skipped_frequency: { color: '#92400E', bg: '#FEF3C7' },
  skipped_active_conversation: { color: '#92400E', bg: '#FEF3C7' },
  opted_out: { color: '#991B1B', bg: '#FEE2E2' },
};
const DETAIL_STATUSES = ['pending', 'sent', 'delivered', 'read', 'replied', 'declined', 'failed', 'skipped_optout', 'skipped_veto', 'skipped_frequency', 'skipped_active_conversation', 'opted_out'];
const fmtHito = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleString('es-CL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

// Control de frecuencia (días): espejo del WHATSAPP_PUSH_FREQUENCY_DAYS del
// backend — Erwin mantiene ambos .env con el mismo valor. Solo presentación:
// la regla real la aplica el backend.
const PUSH_FREQUENCY_DAYS = parseInt(process.env.REACT_APP_PUSH_FREQUENCY_DAYS, 10) || 30;

const AtsCampaignDialog = ({ open, onClose, positionId }) => {
  const { t } = useTranslation();
  const tc = (k, o) => t(`ats.captation.campaigns.${k}`, o);
  const { authToken, showAlert, project } = useStore.getState();
  const authHeaders = { headers: { Authorization: `Bearer ${authToken}` } };

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Sin WhatsApp en la organización (sin número propio ni el compartido
  // permitido por un super-admin): se pueden cargar bases, no enviarlas
  const whatsapp = useWhatsappChannel(open);

  // Carga nueva
  const [file, setFile] = useState(null);
  // Fuente BD de candidatos (2026-08-24): buscador + selección, mezclable con el Excel
  const [dbSearch, setDbSearch] = useState('');
  const [dbResults, setDbResults] = useState(null); // null = aún sin búsqueda
  const [dbSearching, setDbSearching] = useState(false);
  const [dbSelected, setDbSelected] = useState([]); // [{ id, name, phone }]
  const [name, setName] = useState('');
  const [origen, setOrigen] = useState('');
  const [optIn, setOptIn] = useState(false);
  // Preview del draft recién cargado
  const [preview, setPreview] = useState(null); // { campaignId, counts, invalid, excluded, warnings }
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  // Fase B: confirmación de envío + refresco de contadores en vivo
  const [confirmSendTarget, setConfirmSendTarget] = useState(null); // campaña a enviar (dialog)
  const [sendingId, setSendingId] = useState(null);
  // Detalle por contacto (2026-08-24): dialog con lista paginada + filtro
  const [detailTarget, setDetailTarget] = useState(null); // campaña abierta en detalle
  const [detailFilter, setDetailFilter] = useState('');
  const [detailContacts, setDetailContacts] = useState([]);
  const [detailCursor, setDetailCursor] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/captation/${positionId}/campaigns`, authHeaders);
      if (data.response?.status_response === 'OK') setCampaigns(data.response.data || []);
    } catch (err) {
      console.error('Error cargando campañas:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionId]);

  // Mientras haya una campaña enviando, refrescar contadores cada 10 s
  useEffect(() => {
    if (!open) return undefined;
    const hasLive = campaigns.some((c) => c.status === 'sending');
    if (!hasLive) return undefined;
    const timer = setInterval(loadCampaigns, 10000);
    return () => clearInterval(timer);
  }, [open, campaigns, loadCampaigns]);

  // Planilla de ejemplo: solo Nombre + Teléfono (columnas obligatorias del
  // PLAN §3.3), con formatos de teléfono variados para mostrar que todo se
  // normaliza. Se genera en el navegador — sin backend.
  const handleDownloadExample = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['Nombre', 'Teléfono'],
      ['María Pérez', '+56 9 1234 5678'],
      ['Juan Soto', '912345679'],
      ['Camila Rojas', '9.1234.5680'],
    ]);
    ws['!cols'] = [{ wch: 24 }, { wch: 18 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contactos');
    XLSX.writeFile(wb, 'plantilla_campana.xlsx');
  };

  // Detalle por contacto: primera página o "cargar más" (cursor)
  const loadDetail = async (campaign, filter, cursor = null) => {
    setDetailLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.set('status', filter);
      if (cursor) params.set('cursor', cursor);
      const { data } = await api.get(
        `/captation/campaigns/${campaign.id}/contacts?${params.toString()}`,
        authHeaders
      );
      const payload = data.response?.data || {};
      setDetailContacts((prev) => (cursor ? [...prev, ...(payload.contacts || [])] : (payload.contacts || [])));
      setDetailCursor(payload.nextCursor || null);
    } catch (err) {
      console.error('Error cargando detalle de contactos:', err);
      showAlert(tc('detailError'), 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  const openDetail = (campaign) => {
    setDetailTarget(campaign);
    setDetailFilter('');
    setDetailContacts([]);
    setDetailCursor(null);
    loadDetail(campaign, '');
  };

  const changeDetailFilter = (value) => {
    setDetailFilter(value);
    setDetailContacts([]);
    setDetailCursor(null);
    if (detailTarget) loadDetail(detailTarget, value);
  };

  const handleSend = async (campaignId) => {
    setSendingId(campaignId);
    try {
      const { data } = await api.post(`/captation/campaigns/${campaignId}/send`, {}, authHeaders);
      if (data.response?.status_response === 'OK') {
        showAlert(tc('sendStarted'), 'success');
      } else {
        showAlert(data.response?.message || tc('sendError'), 'error');
      }
    } catch (err) {
      console.error('Error iniciando envío:', err);
      showAlert(err.response?.data?.response?.message || tc('sendError'), 'error');
    } finally {
      setSendingId(null);
      setConfirmSendTarget(null);
      loadCampaigns();
    }
  };

  useEffect(() => {
    if (open) {
      setFile(null); setName(''); setOrigen(''); setOptIn(false);
      setDbSearch(''); setDbResults(null); setDbSelected([]);
      setPreview(null); setConfirmDeleteId(null);
      loadCampaigns();
    }
  }, [open, loadCampaigns]);

  const handleDbSearch = async () => {
    const term = dbSearch.trim();
    if (term.length < 2) return;
    setDbSearching(true);
    try {
      const { data } = await api.get(
        `/applicants/search?searchTerm=${encodeURIComponent(term)}&projectId=${project}`,
        authHeaders
      );
      setDbResults((data.response?.data || []).slice(0, 30));
    } catch (err) {
      console.error('Error buscando candidatos:', err);
      showAlert(tc('dbSearchError'), 'error');
    } finally {
      setDbSearching(false);
    }
  };

  const toggleDbCandidate = (a) => {
    const id = a.id || a.uid;
    setDbSelected((prev) => (prev.some((x) => x.id === id)
      ? prev.filter((x) => x.id !== id)
      : [...prev, { id, name: a.name || a.names || a.email || id, phone: a.phone || null }]));
  };

  const handleUpload = async () => {
    if (!file && dbSelected.length === 0) return;
    setUploading(true);
    try {
      const form = new FormData();
      if (file) form.append('sheet', file);
      if (dbSelected.length > 0) form.append('applicantIds', JSON.stringify(dbSelected.map((x) => x.id)));
      if (name.trim()) form.append('name', name.trim());
      if (origen.trim()) form.append('origen', origen.trim());
      if (optIn) form.append('optInDeclared', 'true');
      const { data } = await api.post(`/captation/${positionId}/campaigns`, form, authHeaders);
      if (data.response?.status_response === 'OK') {
        setPreview(data.response.data);
      } else {
        showAlert(data.response?.message || tc('uploadError'), 'error');
      }
    } catch (err) {
      console.error('Error cargando campaña:', err);
      showAlert(err.response?.data?.response?.message || tc('uploadError'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await api.put(`/captation/campaigns/${preview.campaignId}/confirm`, {}, authHeaders);
      showAlert(tc('confirmed'), 'success');
      setPreview(null); setFile(null); setName(''); setOrigen(''); setOptIn(false);
      setDbSearch(''); setDbResults(null); setDbSelected([]);
      loadCampaigns();
    } catch (err) {
      console.error('Error confirmando campaña:', err);
      showAlert(tc('confirmError'), 'error');
    } finally {
      setConfirming(false);
    }
  };

  const handleDiscard = async () => {
    setConfirming(true);
    try {
      await api.delete(`/captation/campaigns/${preview.campaignId}`, authHeaders);
      setPreview(null);
    } catch (err) {
      console.error('Error descartando campaña:', err);
    } finally {
      setConfirming(false);
    }
  };

  const handleDelete = async (campaignId) => {
    try {
      await api.delete(`/captation/campaigns/${campaignId}`, authHeaders);
      setConfirmDeleteId(null);
      loadCampaigns();
    } catch (err) {
      console.error('Error eliminando campaña:', err);
      showAlert(tc('deleteError'), 'error');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {tc('title')}
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {tc('subtitle')}
        </Typography>
        {!whatsapp.usable && (
          <Alert severity="warning" sx={{ mb: 2 }}>{tc('whatsappUnavailable')}</Alert>
        )}

        {/* ── Campañas existentes ── */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
        ) : campaigns.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            {campaigns.map((c) => {
              const chip = statusChip[c.status] || statusChip.draft;
              return (
                <Paper key={c.id} elevation={0} sx={{ p: 1.25, border: '1px solid #E5E7EB', borderRadius: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {c.name || tc('unnamed')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tc('campaignCounts', { valid: c.counts?.valid ?? 0, existing: c.counts?.existing ?? 0, invalid: (c.counts?.invalid ?? 0) + (c.counts?.excluded ?? 0) })}
                        {c.origen ? ` · ${c.origen}` : ''}
                      </Typography>
                      {['sending', 'paused_tier', 'completed'].includes(c.status) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {tc('sendCounts', {
                            sent: c.counts?.sent ?? 0,
                            delivered: c.counts?.delivered ?? 0,
                            read: c.counts?.read ?? 0,
                            replied: c.counts?.replied ?? 0,
                            declined: c.counts?.declined ?? 0,
                            failed: (c.counts?.failed ?? 0) + (c.counts?.skipped ?? 0),
                            optedOut: c.counts?.optedOut ?? 0,
                          })}
                        </Typography>
                      )}
                      {/* 'ready' con motivo: se detuvo porque la organización quedó sin WhatsApp */}
                      {['paused_tier', 'ready'].includes(c.status) && c.pausedReason && (
                        <Typography variant="caption" sx={{ display: 'block', color: '#92400E' }}>
                          {c.pausedReason}
                        </Typography>
                      )}
                      {['sending', 'paused_tier', 'completed'].includes(c.status) && (
                        <Typography
                          variant="caption"
                          color="primary"
                          onClick={() => openDetail(c)}
                          sx={{ cursor: 'pointer', userSelect: 'none', display: 'inline-block', mt: 0.25 }}
                        >
                          {tc('detailShow')}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={tc(`status.${c.status}`)}
                      size="small"
                      sx={{ backgroundColor: chip.bg, color: chip.color, fontWeight: 700, fontSize: '0.65rem' }}
                    />
                    {c.status === 'ready' && (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!whatsapp.usable}
                        onClick={() => setConfirmSendTarget(c)}
                        sx={{ fontSize: '0.7rem', backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
                      >
                        {tc('sendButton')}
                      </Button>
                    )}
                    {confirmDeleteId === c.id ? (
                      <>
                        <Button size="small" color="error" onClick={() => handleDelete(c.id)} sx={{ fontSize: '0.7rem' }}>
                          {tc('deleteConfirm')}
                        </Button>
                        <Button size="small" color="inherit" onClick={() => setConfirmDeleteId(null)} sx={{ fontSize: '0.7rem' }}>
                          {t('cancel')}
                        </Button>
                      </>
                    ) : (
                      <IconButton size="small" onClick={() => setConfirmDeleteId(c.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* ── Preview del draft o formulario de carga nueva ── */}
        {preview ? (
          <Box>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>{tc('previewTitle')}</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
              <Chip label={tc('previewValid', { count: preview.counts.valid })} size="small" sx={{ backgroundColor: '#DCFCE7', color: '#16A34A', fontWeight: 700 }} />
              <Chip label={tc('previewExisting', { count: preview.counts.existing })} size="small" sx={{ backgroundColor: '#EDE9FE', color: '#4C1D95', fontWeight: 700 }} />
              {(preview.counts.fromDb ?? 0) > 0 && (
                <Chip label={tc('previewFromDb', { count: preview.counts.fromDb })} size="small" sx={{ backgroundColor: '#DBEAFE', color: '#1D4ED8', fontWeight: 700 }} />
              )}
              {preview.counts.invalid > 0 && (
                <Chip label={tc('previewInvalid', { count: preview.counts.invalid })} size="small" sx={{ backgroundColor: '#FEF3C7', color: '#92400E', fontWeight: 700 }} />
              )}
              {preview.counts.excluded > 0 && (
                <Chip label={tc('previewExcluded', { count: preview.counts.excluded })} size="small" sx={{ backgroundColor: '#FEE2E2', color: '#991B1B', fontWeight: 700 }} />
              )}
            </Box>
            {[...(preview.invalid || []), ...(preview.excluded || [])].slice(0, 20).map((r, i) => (
              <Typography key={i} variant="caption" sx={{ display: 'block', color: '#92400E' }}>
                • {r.row ? tc('rowLabel', { row: r.row }) : tc('dbRowLabel')}: {r.reason}
              </Typography>
            ))}
            {(preview.warnings || []).map((w, i) => (
              <Typography key={i} variant="caption" sx={{ display: 'block', color: '#92400E', fontWeight: 600 }}>⚠ {w}</Typography>
            ))}
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleConfirm}
                disabled={confirming || preview.counts.valid === 0}
                startIcon={confirming ? <CircularProgress size={12} color="inherit" /> : null}
                sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4BE0' } }}
              >
                {tc('confirmButton', { count: preview.counts.valid })}
              </Button>
              <Button size="small" color="inherit" onClick={handleDiscard} disabled={confirming}>
                {tc('discardButton')}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" fontWeight={700}>{tc('newTitle')}</Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Button component="label" variant="outlined" size="small" startIcon={<UploadIcon />}>
                {file ? file.name : tc('fileButton')}
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </Button>
              <Button size="small" onClick={handleDownloadExample} sx={{ fontSize: '0.75rem' }}>
                {tc('exampleButton')}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              {tc('fileHint')}
            </Typography>

            {/* ── Fuente BD: buscador de candidatos del proyecto (mezclable con el Excel) ── */}
            <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>{tc('dbTitle')}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small" fullWidth
                placeholder={tc('dbSearchPlaceholder')}
                value={dbSearch}
                onChange={(e) => setDbSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleDbSearch(); } }}
              />
              <Button
                size="small" variant="outlined"
                onClick={handleDbSearch}
                disabled={dbSearching || dbSearch.trim().length < 2}
              >
                {dbSearching ? <CircularProgress size={14} /> : tc('dbSearchButton')}
              </Button>
            </Box>
            {dbResults !== null && (
              dbResults.length === 0 ? (
                <Typography variant="caption" color="text.secondary">{tc('dbNoResults')}</Typography>
              ) : (
                <Box sx={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 1.5, px: 1, py: 0.5 }}>
                  {dbResults.map((a) => {
                    const id = a.id || a.uid;
                    const checked = dbSelected.some((x) => x.id === id);
                    return (
                      <Box key={id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Checkbox size="small" checked={checked} onChange={() => toggleDbCandidate(a)} sx={{ p: 0.5 }} />
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                          <Typography variant="body2" noWrap>{a.name || a.names || a.email || id}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {a.phone || tc('dbNoPhone')}{a.email ? ` · ${a.email}` : ''}
                          </Typography>
                        </Box>
                        {!a.phone && (
                          <Chip label={tc('dbNoPhone')} size="small" sx={{ backgroundColor: '#FEF3C7', color: '#92400E' }} />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )
            )}
            {dbSelected.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {dbSelected.map((x) => (
                  <Chip key={x.id} label={x.name} size="small" onDelete={() => setDbSelected((prev) => prev.filter((y) => y.id !== x.id))} />
                ))}
              </Box>
            )}

            <TextField
              size="small" fullWidth
              label={tc('nameLabel')}
              placeholder={tc('namePlaceholder')}
              helperText={tc('nameHint')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              size="small" fullWidth
              label={tc('origenLabel')}
              placeholder={tc('origenPlaceholder')}
              helperText={tc('origenHint')}
              value={origen}
              onChange={(e) => setOrigen(e.target.value)}
            />
            <Box>
              <FormControlLabel
                control={<Checkbox size="small" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} />}
                label={<Typography variant="body2">{tc('optInLabel')}</Typography>}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
                {tc('optInHint')}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      {!preview && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} color="inherit" size="small">{t('cancel')}</Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleUpload}
            disabled={(!file && dbSelected.length === 0) || uploading}
            startIcon={uploading ? <CircularProgress size={12} color="inherit" /> : null}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4BE0' } }}
          >
            {tc('uploadButton')}
          </Button>
        </DialogActions>
      )}
    {/* Detalle por contacto: quién recibió, leyó, respondió (2026-08-24) */}
      <Dialog open={Boolean(detailTarget)} onClose={() => setDetailTarget(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {tc('detailTitle', { name: detailTarget?.name || tc('unnamed') })}
          <IconButton size="small" onClick={() => setDetailTarget(null)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            select size="small" sx={{ mb: 1.5, minWidth: 260 }}
            label={tc('detailFilterLabel')}
            value={detailFilter}
            onChange={(e) => changeDetailFilter(e.target.value)}
          >
            <MenuItem value="">{tc('detailFilterAll')}</MenuItem>
            {DETAIL_STATUSES.map((st) => (
              <MenuItem key={st} value={st}>{tc(`contactStatus.${st}`, { days: PUSH_FREQUENCY_DAYS })}</MenuItem>
            ))}
          </TextField>

          {detailContacts.length === 0 && !detailLoading ? (
            <Typography variant="body2" color="text.secondary">{tc('detailEmpty')}</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {detailContacts.map((ct) => {
                const cch = contactStatusChip[ct.status] || contactStatusChip.pending;
                const hitos = [
                  ct.sentAt && tc('detailSentAt', { time: fmtHito(ct.sentAt) }),
                  ct.deliveredAt && tc('detailDeliveredAt', { time: fmtHito(ct.deliveredAt) }),
                  ct.readAt && tc('detailReadAt', { time: fmtHito(ct.readAt) }),
                  ct.repliedAt && tc('detailRepliedAt', { time: fmtHito(ct.repliedAt) }),
                  ct.declinedAt && tc('detailDeclinedAt', { time: fmtHito(ct.declinedAt) }),
                ].filter(Boolean);
                return (
                  <Box key={ct.phone} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderBottom: '1px solid #F3F4F6' }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {ct.name || ct.phoneFormatted}
                        {ct.name ? (
                          <Typography component="span" variant="caption" color="text.secondary"> · {ct.phoneFormatted}</Typography>
                        ) : null}
                        {ct.fromDb && (
                          <Typography component="span" variant="caption" sx={{ color: '#1D4ED8' }}> · BD</Typography>
                        )}
                      </Typography>
                      {hitos.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {hitos.join(' · ')}
                        </Typography>
                      )}
                      {ct.reason && (
                        <Typography variant="caption" sx={{ display: 'block', color: '#92400E' }}>
                          {ct.reason}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={tc(`contactStatus.${ct.status}`, { defaultValue: ct.status, days: PUSH_FREQUENCY_DAYS })}
                      size="small"
                      sx={{ backgroundColor: cch.bg, color: cch.color, fontWeight: 700, fontSize: '0.65rem', flexShrink: 0 }}
                    />
                  </Box>
                );
              })}
              {detailLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}><CircularProgress size={18} /></Box>
              )}
              {detailCursor && !detailLoading && (
                <Button size="small" onClick={() => loadDetail(detailTarget, detailFilter, detailCursor)} sx={{ alignSelf: 'center', mt: 1 }}>
                  {tc('detailLoadMore')}
                </Button>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDetailTarget(null)} color="inherit" size="small">{t('close', 'Cerrar')}</Button>
        </DialogActions>
      </Dialog>

    {/* Confirmación de envío (modal — el envío cuesta plata por mensaje) */}
      <Dialog
        open={Boolean(confirmSendTarget)}
        onClose={() => { if (!sendingId) setConfirmSendTarget(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {tc('sendConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {tc('sendConfirmBody', {
              name: confirmSendTarget?.name || tc('unnamed'),
              count: confirmSendTarget?.counts?.valid ?? 0,
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {tc('sendConfirmNote', { days: PUSH_FREQUENCY_DAYS })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSendTarget(null)} disabled={Boolean(sendingId)}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={Boolean(sendingId)}
            onClick={() => handleSend(confirmSendTarget.id)}
            startIcon={sendingId ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
          >
            {tc('sendConfirmButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AtsCampaignDialog;
